'use client';

import { type CommentDto } from '@/domain/roadmap/types';
import { toLocaleDateTimeString } from '@/utils/datetime';
import { Avatar } from '@andritzot/metris-web-ui/data-display/avatar';
import { ConfirmDialog } from '@andritzot/metris-web-ui/feedback/confirm-dialog';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { TextField } from '@andritzot/metris-web-ui/inputs/textfield';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import { useState } from 'react';
import Linkify from 'react-linkify';

interface MessageCardProps {
  isOwnedByUser: boolean;
  comment: CommentDto;
  onEdit: (value: string, id: number) => void;
  onDelete: (id: number) => void;
}

const actionButtonSx = {
  px: '0.5em',
  py: 0,
  fontSize: '12px',
  letterSpacing: '0.3px',
  m: 0,
  ml: '0.5rem',
};

const deleteButtonSx = {
  color: 'rgb(255, 0, 0)',
  px: '0.5em',
  py: 0,
  fontSize: '12px',
  letterSpacing: '0.3px',
  m: 0,
};

const MessageCard = ({ isOwnedByUser, comment, onEdit, onDelete }: MessageCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const [value, setValue] = useState(comment.comment);

  const handleEdit = () => {
    onEdit(value, comment.idComment);
    setIsEditing(false);
  };
  const handleCloseConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleDelete = () => {
    setIsConfirmDialogOpen(true);
  };

  return (
    <Box component="li" sx={{ p: '12px', m: 0, listStyleType: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          mr: '-1rem',
          ml: '-1rem',
          mt: '-1rem',
          flexDirection: isOwnedByUser ? 'row-reverse' : 'row',
        }}
      >
        <Box sx={{ pt: '1.5rem', flex: '0 0 auto', alignSelf: 'center' }}>
          <Avatar sx={{ backgroundColor: '#2196F3', color: '#ffffff', marginRight: '0.5rem' }}>
            <PersonOutlined />
          </Avatar>
        </Box>
        <Box sx={{ flexGrow: 1, flexBasis: 0, p: '0.1rem 1rem' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '0.5em', alignItems: 'flex-end' }}>
            <Box sx={{ fontWeight: 700, fontSize: '12px', letterSpacing: '0.3px', color: '#8a8a8a' }}>
              {comment.user.name} -{' '}
            </Box>
            <Box sx={{ fontSize: '12px', letterSpacing: '0.3px', color: '#8a8a8a', ml: '0.2rem' }}>
              {toLocaleDateTimeString(comment.timestamp)}
            </Box>
            {isOwnedByUser && !isEditing && (
              <>
                <Button
                  onClick={() => {
                    setIsEditing(true);
                  }}
                  variant="text"
                  sx={actionButtonSx}
                >
                  Edit
                </Button>
                <Button onClick={handleDelete} variant="text" sx={deleteButtonSx}>
                  Delete
                </Button>
              </>
            )}
            {isEditing && (
              <Button variant="outlined" sx={actionButtonSx} onClick={handleEdit}>
                Save
              </Button>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: '#ebebef',
              position: 'relative',
              p: '0.5em',
              borderRadius: '2px',
              wordBreak: 'break-word',
            }}
          >
            {isEditing ? (
              <TextField
                sx={{
                  width: '100%',
                  height: '100%',
                  resize: 'none',
                  border: 'none',
                  outline: 'none',
                  fontSize: '12px',
                  letterSpacing: '0.3px',
                }}
                rows={2}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
            ) : (
              <Linkify
                componentDecorator={(decoratedHref, decoratedText, key) => (
                  <a target="blank" href={decoratedHref} key={key}>
                    {decoratedText}
                  </a>
                )}
              >
                {comment.comment}
              </Linkify>
            )}
          </Box>
        </Box>
      </Box>
      {isConfirmDialogOpen && (
        <ConfirmDialog
          title="Confirm Delete"
          message="Are you sure you want to delete this comment?"
          open={isConfirmDialogOpen}
          onClose={handleCloseConfirmDialog}
          onConfirm={() => {
            onDelete(comment.idComment);
            handleCloseConfirmDialog();
          }}
        />
      )}
    </Box>
  );
};

export default MessageCard;
